import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, User, Stethoscope, HeartPulse, MonitorPlay, CalendarClock, Activity, Eye } from "lucide-react";
import { api, apiErr } from "../api";
import { BRAND } from "../brand";
import { EMPTY_PATIENT, SEXO, SANGRE, ESTADO_CIVIL, SPEC } from "../fields";
import { PanelPage, Header, PageTitle, Section, Field, Area, Select, FullLoader, calcAge } from "../ui";
import MediaField from "../components/MediaField";

const ICONS = { stetho: Stethoscope, heart: HeartPulse, visor: MonitorPlay };
import NextAppointmentModal from "../components/NextAppointmentModal";
import FollowUpModal from "../components/FollowUpModal";

export default function PatientForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const nav = useNavigate();
  const [form, setForm] = useState(editing ? null : { ...EMPTY_PATIENT, consulta_lugar: BRAND.defaultPlace || "" });
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    if (!editing) return;
    api.get(`/patients/${id}`).then((r) => { setForm({ ...EMPTY_PATIENT, ...r.data }); setSaved(r.data); })
      .catch((e) => { toast.error(apiErr(e, "No se pudo cargar")); nav("/dashboard"); });
  }, [id, editing, nav]);

  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  if (!form) return <PanelPage><Header /><FullLoader /></PanelPage>;

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setDirty(true); };
  const setBirth = (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, fecha_nacimiento: v, edad: calcAge(v) || f.edad }));
    setDirty(true);
  };

  // En pacientes ya guardados, los archivos se guardan al instante
  const setMedia = (field) => async (list) => {
    setForm((f) => ({ ...f, [field]: list }));
    if (saved) {
      try { const { data } = await api.patch(`/patients/${saved.id}`, { [field]: list }); setSaved(data); toast.success("Archivos guardados"); }
      catch (e) { toast.error(apiErr(e, "No se pudieron guardar los archivos")); }
    } else setDirty(true);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    setBusy(true);
    try {
      const { data } = saved ? await api.put(`/patients/${saved.id}`, form) : await api.post("/patients", form);
      setSaved(data); setDirty(false);
      toast.success(saved ? "Ficha actualizada" : "Paciente guardado");
      if (!editing) nav(`/pacientes/${data.id}/editar`, { replace: true });
    } catch (err) { toast.error(apiErr(err, "No se pudo guardar")); }
    finally { setBusy(false); }
  };

  const SaveBtn = ({ className = "" }) => (
    <button type="submit" disabled={busy} className={`pn-btn pn-btn-primary ${className}`}>
      {busy ? <Loader2 size={16} className="pn-spin" /> : <Save size={16} />} {busy ? "Guardando…" : saved ? "Guardar cambios" : "Guardar paciente"}
    </button>
  );

  return (
    <PanelPage>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <Link to={saved ? `/pacientes/${saved.id}` : "/dashboard"} className="inline-flex items-center gap-1.5 text-sm pn-sub mb-4"><ArrowLeft size={15} /> {saved ? "Volver a la ficha" : "Dashboard"}</Link>
        <PageTitle eyebrow={saved ? "Editar ficha" : "Nuevo paciente"} title={saved ? form.nombre || "Ficha del paciente" : "Ficha del paciente"}
          sub={dirty ? <span style={{ color: "var(--warn)" }}>Hay cambios sin guardar</span> : saved ? "Todos los cambios guardados" : null}>
          {saved && <>
            <button type="button" onClick={() => setModal("appt")} className="pn-btn pn-btn-soft"><CalendarClock size={16} /> Próxima consulta</button>
            <button type="button" onClick={() => setModal("follow")} className="pn-btn pn-btn-ghost"><Activity size={16} /> Seguimiento</button>
            <Link to={`/visor/${saved.id}`} className="pn-btn pn-btn-ghost"><Eye size={16} /> Visor</Link>
          </>}
        </PageTitle>

        <form onSubmit={submit} className="space-y-6">
          <Section title="Datos generales" icon={User} cols={3}>
            <div className="sm:col-span-2 lg:col-span-2"><Field label="Nombre completo" required value={form.nombre} onChange={set("nombre")} /></div>
            <Field label="Cédula de identidad" value={form.cedula} onChange={set("cedula")} />
            <Select label="Sexo" value={form.sexo} onChange={set("sexo")} options={SEXO} />
            <Field label="Fecha de nacimiento" type="date" value={form.fecha_nacimiento} onChange={setBirth} />
            <Field label="Edad" value={form.edad} onChange={set("edad")} placeholder="Se calcula con la fecha de nacimiento" />
            <Select label="Tipo de sangre" value={form.tipo_sangre} onChange={set("tipo_sangre")} options={SANGRE} />
            <Select label="Estado civil" value={form.estado_civil} onChange={set("estado_civil")} options={ESTADO_CIVIL} />
            <Field label="Ocupación" value={form.ocupacion} onChange={set("ocupacion")} />
            <Field label="Teléfono (WhatsApp)" value={form.telefono} onChange={set("telefono")} placeholder="09XXXXXXXX" inputMode="tel" />
            <Field label="Email" type="email" value={form.email} onChange={set("email")} placeholder="Para enviarle sus citas" />
            <Field label="Ciudad" value={form.ciudad} onChange={set("ciudad")} />
            <div className="sm:col-span-2 lg:col-span-2"><Field label="Dirección" value={form.direccion} onChange={set("direccion")} /></div>
            <Field label="Seguro médico" value={form.seguro} onChange={set("seguro")} />
            <Field label="Contacto de emergencia" value={form.contacto_emergencia} onChange={set("contacto_emergencia")} />
            <Field label="Teléfono de emergencia" value={form.telefono_emergencia} onChange={set("telefono_emergencia")} />
            <Field label="Fecha de consulta" type="date" value={form.fecha_consulta} onChange={set("fecha_consulta")} />
            <Field label="Lugar de consulta" value={form.consulta_lugar} onChange={set("consulta_lugar")} />
          </Section>

          {SPEC.sections.map((sec, si) => (
            <Section key={sec.title} title={sec.title} icon={ICONS[sec.icon]}>
              {si === 0 && (
                <label className="sm:col-span-2 flex items-start gap-3 pn-soft-brand rounded-xl px-4 py-3 cursor-pointer">
                  <input type="checkbox" checked={!!form.enfermedades_cronicas} className="w-5 h-5 mt-0.5" style={{ accentColor: "var(--brand)" }}
                    onChange={(e) => { setForm((f) => ({ ...f, enfermedades_cronicas: e.target.checked })); setDirty(true); }} />
                  <span>
                    <span className="block text-sm font-semibold pn-ink">{SPEC.chronic.label}</span>
                    <span className="block text-xs pn-sub">{SPEC.chronic.hint}</span>
                  </span>
                </label>
              )}
              {sec.fields.map(([k, label, type, full]) => (type === "area"
                ? <Area key={k} label={label} value={form[k]} onChange={set(k)} full={full} rows={full ? 3 : 2} />
                : <Field key={k} label={label} value={form[k]} onChange={set(k)} full={full} />))}
            </Section>
          ))}

          <Section title="Archivos: imágenes, videos e informes" icon={MonitorPlay}>
            {Object.entries(SPEC.media).map(([field, m], i, arr) => (
              <MediaField key={field} label={m.label} accept={m.accept} media={form[field]} onChange={setMedia(field)}
                hint={i === arr.length - 1 ? (saved ? "Los archivos se guardan al subirlos." : "Los archivos se guardan junto con el paciente.") : undefined} />
            ))}
            <Area label="Notas internas" value={form.notas} onChange={set("notas")} full rows={2} />
          </Section>

          <div className="sticky bottom-0 py-4 -mx-4 px-4 sm:mx-0 sm:px-0 flex flex-wrap gap-3 items-center" style={{ background: "linear-gradient(transparent, var(--page) 35%)" }}>
            <SaveBtn className="px-6 py-3" />
            {saved && <Link to={`/pacientes/${saved.id}`} className="pn-btn pn-btn-ghost py-3">Ver ficha</Link>}
          </div>
        </form>
      </main>
      {modal === "appt" && saved && <NextAppointmentModal patient={{ ...saved, ...form }} onClose={() => setModal(null)} onSaved={() => {}} />}
      {modal === "follow" && saved && <FollowUpModal patient={saved} onClose={() => setModal(null)} />}
    </PanelPage>
  );
}
